import { useEffect, useRef } from 'react';

import { useRedemptionState } from '@/features/code-redemption';
import { ApiError } from '@/shared/lib/apiClient';

import { getCodeStatus } from '../api/getCodeStatus';

/**
 * 부팅 시 저장된 유저 코드의 유효성 검증 + 진행 상태 동기화 훅.
 *
 * 새로고침/탭 복원 시 localStorage 에 보관 중인 `userCode` 가 서버에 여전히 존재하는지
 * 확인하고, 클라이언트 카운트를 서버 권위 값으로 갱신한다.
 *
 * 동작:
 * - 마운트 시 `state.userCode` 가 있으면 → `GET /api/code/status/{userCode}` 한 번만 호출.
 * - 200: `codeCount`/`downloadUrl` 을 클라 상태와 머지 (서버 우선).
 * - 404: 서버에서 코드가 사라진 경우 → localStorage 정리(`reset`)해 자동 재발급 흐름으로 보낸다.
 * - 그 외 오류(네트워크 등)는 사일런트 — 캐시된 카운트로 화면을 그대로 노출한다.
 *
 * StrictMode 의 이중 마운트로 인한 중복 호출은 `useRef` 가드로 차단한다.
 */
export function useParticipantBootstrap(): void {
  const { state, applyStatus, reset } = useRedemptionState();
  const userCode = state.userCode;
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (userCode === null || userCode === '') {
      // 코드가 없으면 별도 작업 없음 — 자동 발급 위젯이 처리한다.
      // 코드가 새로 발급되면 이 effect 가 다시 실행되며 그때 가드가 열려야 하므로
      // null 진입 시 가드를 풀어준다.
      triggeredRef.current = false;
      return;
    }
    if (triggeredRef.current) {
      return;
    }
    triggeredRef.current = true;

    void (async (): Promise<void> => {
      try {
        const status = await getCodeStatus(userCode);
        applyStatus({
          codeCount: status.codeCount,
          downloadUrl: status.downloadUrl,
        });
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          // 서버가 더 이상 이 코드를 모름 — 깨끗이 정리해 자동 재발급 흐름으로 안내.
          reset();
          triggeredRef.current = false;
          return;
        }
        // 네트워크/서버 일시 장애 — 캐시된 상태를 유지하고 다음 진입 때 다시 시도.
      }
    })();
  }, [userCode, applyStatus, reset]);
}
